import PluginUtils from 'owa-editor-plugin-utils/lib/schema/PluginUtils';
import EditorPluginEx from 'owa-editor-plugin-utils/lib/schema/EditorPluginEx';
import { lazySetTableHeaderRow } from 'owa-editor';
import { ContextMenuProvider, IEditor, TableFormat, TableOperation } from 'roosterjs-editor-types';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import loc from 'owa-localize';
import {
    tableMenuInsert,
    tableMenuInsertAbove,
    tableMenuInsertBelow,
    tableMenuInsertLeft,
    tableMenuInsertRight,
    tableMenuDelete,
    tableMenuDeleteTable,
    tableMenuDeleteColumn,
    tableMenuDeleteRow,
    tableMenuMerge,
    tableMenuMergeAbove,
    tableMenuMergeBelow,
    tableMenuMergeLeft,
    tableMenuMergeRight,
    tableMenuSplit,
    tableMenuSplitHorizontally,
    tableMenuSplitVertically,
    tableMenuStyle,
    tableMenuStyleDefault,
    tableMenuStyleGrid,
    tableMenuStyleLightLines,
    tableMenuStyleTwoTones,
    tableMenuStyleLightBands,
    tableMenuStyleClear,
    tableMenuHeaderRow,
} from './TableEditContextMenu.locstring.json';

const TABLE_CELL_TAG_NAME = 'TD';
const TABLE_HEADER_TAG_NAME = 'TH';

function createTableFormat(
    bgColorEven?: string,
    bgColorOdd?: string,
    topBorder?: string,
    bottomBorder?: string,
    verticalBorder?: string
): TableFormat {
    return {
        bgColorEven: bgColorEven,
        bgColorOdd: bgColorOdd,
        topBorderColor: topBorder,
        bottomBorderColor: bottomBorder,
        verticalBorderColor: verticalBorder,
    };
}

const TABLE_FORMAT_MAP: { [name: string]: TableFormat } = {
    Default: createTableFormat('#FFF', '#FFF', '#ABABAB', '#ABABAB', '#ABABAB'),
    LightLines: createTableFormat('#FFF', '#FFF', null, '#92C0E0'),
    TwoTones: createTableFormat('#C0E4FF', '#FFF'),
    LightBands: createTableFormat('#D8D8D8', '#FFF'),
    Grid: createTableFormat('#D8D8D8', '#FFF', '#ABABAB', '#ABABAB', '#ABABAB'),
    Clear: createTableFormat('#FFF', '#FFF'),
};

export default class TableEditPlugin
    implements EditorPluginEx, ContextMenuProvider<IContextualMenuItem> {
    private editor: IEditor;
    private pluginUtils: PluginUtils;
    private range: Range;
    private invokedTableCell: HTMLTableCellElement;
    private hasHeaders: boolean;

    getName() {
        return 'TableEdit';
    }

    initialize(editor: IEditor) {
        this.editor = editor;
    }

    dispose() {
        this.editor = null;
        this.invokedTableCell = null;
    }

    setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
    }

    getContextMenuItems(node: Node): IContextualMenuItem[] | null {
        let td = this.editor.getElementAtCursor(
            `${TABLE_CELL_TAG_NAME},${TABLE_HEADER_TAG_NAME}`,
            node
        ) as HTMLTableCellElement;

        if (td) {
            this.range = this.editor.getSelectionRange();
            this.invokedTableCell = td;
            const vTable = this.pluginUtils.createVTable(this.invokedTableCell);
            this.hasHeaders = true;
            for (let idx = 0; idx < vTable.cells[0].length; idx++) {
                const vCellTD = vTable.cells[0][idx].td;
                // Virtual cells' td can be null due to cell merging. Make sure it exists before checking tagName.
                if (vCellTD && vCellTD.tagName != TABLE_HEADER_TAG_NAME) {
                    this.hasHeaders = false;
                    break;
                }
            }

            return [
                {
                    key: 'insert',
                    name: loc(tableMenuInsert),
                    subMenuProps: {
                        items: [
                            {
                                key: 'insertAbove',
                                name: loc(tableMenuInsertAbove),
                                data: TableOperation.InsertAbove,
                                onClick: this.editTable,
                            },
                            {
                                key: 'insertBelow',
                                name: loc(tableMenuInsertBelow),
                                data: TableOperation.InsertBelow,
                                onClick: this.editTable,
                            },
                            {
                                key: 'insertLeft',
                                name: loc(tableMenuInsertLeft),
                                data: TableOperation.InsertLeft,
                                onClick: this.editTable,
                            },
                            {
                                key: 'insertRight',
                                name: loc(tableMenuInsertRight),
                                data: TableOperation.InsertRight,
                                onClick: this.editTable,
                            },
                        ],
                    },
                },
                {
                    key: 'delete',
                    name: loc(tableMenuDelete),
                    subMenuProps: {
                        items: [
                            {
                                key: 'deleteTable',
                                name: loc(tableMenuDeleteTable),
                                data: TableOperation.DeleteTable,
                                onClick: this.editTable,
                            },
                            {
                                key: 'deleteColumn',
                                name: loc(tableMenuDeleteColumn),
                                data: TableOperation.DeleteColumn,
                                onClick: this.editTable,
                            },
                            {
                                key: 'deleteRow',
                                name: loc(tableMenuDeleteRow),
                                data: TableOperation.DeleteRow,
                                onClick: this.editTable,
                            },
                        ],
                    },
                },
                {
                    key: 'merge',
                    name: loc(tableMenuMerge),
                    subMenuProps: {
                        items: [
                            {
                                key: 'mergeAbove',
                                name: loc(tableMenuMergeAbove),
                                data: TableOperation.MergeAbove,
                                onClick: this.editTable,
                            },
                            {
                                key: 'mergeBelow',
                                name: loc(tableMenuMergeBelow),
                                data: TableOperation.MergeBelow,
                                onClick: this.editTable,
                            },
                            {
                                key: 'mergeLeft',
                                name: loc(tableMenuMergeLeft),
                                data: TableOperation.MergeLeft,
                                onClick: this.editTable,
                            },
                            {
                                key: 'mergeRight',
                                name: loc(tableMenuMergeRight),
                                data: TableOperation.MergeRight,
                                onClick: this.editTable,
                            },
                        ],
                    },
                },
                {
                    key: 'split',
                    name: loc(tableMenuSplit),
                    subMenuProps: {
                        items: [
                            {
                                key: 'splitHorizontally',
                                name: loc(tableMenuSplitHorizontally),
                                data: TableOperation.SplitHorizontally,
                                onClick: this.editTable,
                            },
                            {
                                key: 'splitVertically',
                                name: loc(tableMenuSplitVertically),
                                data: TableOperation.SplitVertically,
                                onClick: this.editTable,
                            },
                        ],
                    },
                },
                {
                    key: 'style',
                    name: loc(tableMenuStyle),
                    subMenuProps: {
                        items: [
                            {
                                key: 'styleDefault',
                                name: loc(tableMenuStyleDefault),
                                data: 'Default',
                                onClick: this.formatTable,
                            },
                            {
                                key: 'styleGrid',
                                name: loc(tableMenuStyleGrid),
                                data: 'Grid',
                                onClick: this.formatTable,
                            },
                            {
                                key: 'styleLightLines',
                                name: loc(tableMenuStyleLightLines),
                                data: 'LightLines',
                                onClick: this.formatTable,
                            },
                            {
                                key: 'styleTwoTones',
                                name: loc(tableMenuStyleTwoTones),
                                data: 'TwoTones',
                                onClick: this.formatTable,
                            },
                            {
                                key: 'styleLightBands',
                                name: loc(tableMenuStyleLightBands),
                                data: 'LightBands',
                                onClick: this.formatTable,
                            },
                            {
                                key: 'styleClear',
                                name: loc(tableMenuStyleClear),
                                data: 'Clear',
                                onClick: this.formatTable,
                            },
                        ],
                    },
                },
                {
                    key: 'headers',
                    name: loc(tableMenuHeaderRow),
                    onClick: this.setTableHeaders,
                    isChecked: this.hasHeaders,
                    canCheck: true,
                },
            ];
        } else {
            return null;
        }
    }

    private setTableHeaders = () => {
        if (this.range && this.editor) {
            lazySetTableHeaderRow.import().then(setTableHeaderRow => {
                setTableHeaderRow(this.editor, this.invokedTableCell, this.hasHeaders);
                this.invokedTableCell = null;
            });
        }
    };

    private editTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        let operation = item.data as TableOperation;
        if (this.range && this.editor) {
            this.editor.select(this.range);
            this.pluginUtils.editTable(this.editor, operation);
            this.range = null;
        }
    };

    private formatTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        let formatName = item.data as string;
        let tableFormat = TABLE_FORMAT_MAP[formatName];
        if (tableFormat && this.range && this.editor) {
            this.editor.select(this.range);
            this.pluginUtils.formatTable(this.editor, tableFormat);
            this.range = null;
        }
    };
}
